import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Coords, Address } from 'components/google-map';
// components
import CreateProjectFormNewUI from './new-ui';
import { SelectChangeEvent } from '@mui/material';
// custom hooks
import { useParseAddress } from 'hooks/useParseAddress';
// api
import { useGetCountryRegionsQuery } from 'redux-store/api/rapid';
import { firstAddressHelper } from 'utils/helpers/firstAddressHelper';
import { countriesSelectValues } from 'utils/constants';

interface OwnProps {
  isFirstProject?: boolean;
  coordinates: Coords | undefined;
  updateCoordinates: (currentCoords: Coords) => void;
  updateAddress: (currentAddress: Address) => void;
  country?: string;
  handleCountryChange: (e: SelectChangeEvent<string>) => void;
  isCreating: boolean;
}

const CreateProjectForm: React.FC<OwnProps> = ({
  isFirstProject,
  coordinates,
  isCreating,
  country,
  updateAddress,
  updateCoordinates,
  handleCountryChange,
}) => {
  const { parseAddress } = useParseAddress();
  const form = useFormContext();

  const { data: states } = useGetCountryRegionsQuery(
    { limit: '100', country: country },
    {
      skip: !country,
      selectFromResult: ({ data, currentData, originalArgs, isError }) => {
        if (!currentData && isError) {
          const state = countriesSelectValues.find(({ value }) => value === originalArgs?.country);
          form.setValue('state', state.label);
          return { data: [{ label: state.label, value: state.label }] };
        }

        return {
          data: data?.data?.map(({ name }) => ({ label: name, value: name })) || [],
        };
      },
    }
  );

  const handleParseAddress = useCallback(
    async (placeId: string) => {
      const { address, city, zip, state, latLng, stateAbbr } = await parseAddress(placeId, states);

      if (latLng) {
        updateCoordinates(latLng);
      }

      if (address) {
        const newAddress = firstAddressHelper(address);

        form.setValue('address', newAddress);
      }
      form.setValue('country', country);
      form.setValue('city', city);
      form.setValue('zip', zip);
      form.setValue('state', state);
      form.setValue('stateAbbr', stateAbbr);
      form.clearErrors(['country', 'city', 'zip', 'state', 'stateAbbr']);
    },
    [parseAddress, states, form, country, updateCoordinates]
  );

  const props = {
    isFirstProject,
    coordinates,
    isCreating,
    country,
    states,
    updateCoordinates,
    handleParseAddress,
    updateAddress,
    handleCountryChange,
  };

  return <CreateProjectFormNewUI {...props} />;
};

export default CreateProjectForm;
